import { useCallback, useEffect, useState } from 'react'

import { useMsal } from '@azure/msal-react'
import { Flex } from '@react-css/flex'
import axios from 'axios'
import { Link, useHistory, useLocation } from 'react-router-dom'

import {
  TEMP_LOG_FUNCTION,
  formatTokenResponseData,
  login,
  msTokenGenerate,
} from '../../../api/auth'
import { useNotifications } from '../../../contexts/notificationProvider'
import { useUserContext } from '../../../contexts/userContext'
import { useAcquireMsalToken } from '../../../hooks/useAcquireMsalToken'
import { useHandleDbTokenResponse } from '../../../hooks/useHandleDbTokenResponse'
import { loginRequest, silentRequest } from '../../../msAuthConfig'
import { saveAuthReturnPath } from '../../../utils/authReturnPath'
import { LoadingIndicator } from '../../common/LoadingIndicator'

import {
  Content,
  LoginButton,
  LoginCard,
  LoginCardBody,
  LoginError,
  LoginLabel,
  LoginMessage,
  LoginWithMicrosoftButton,
  ThirdPartySeparator,
} from './styles'

export const LoginPage = () => {
  const { addNotification } = useNotifications()
  const [localLoad, setLocalLoad] = useState<boolean>(false)
  const [email, setEmail] = useState<string | null>(null)
  const [password, setPassword] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const { instance } = useMsal()
  const acquireMsalToken = useAcquireMsalToken()
  const handleTokenResponse = useHandleDbTokenResponse()
  const { resetUser } = useUserContext()
  const location = useLocation<{
    from?: { pathname: string; search?: string; hash?: string }
  }>()
  const history = useHistory()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper location.state type
  const redirectError = (location.state as any)?.errorMessage

  useEffect(() => {
    if (location.state?.from) {
      saveAuthReturnPath(location.state.from)
    }
  }, [location.state?.from])

  useEffect(() => {
    if (redirectError) {
      addNotification(redirectError, 'error')
      history.replace({ state: {} })
    }
  }, [addNotification, history, redirectError])

  useEffect(() => {
    if (location.hash) {
      // eslint-disable-next-line no-console -- TODO: Replace with proper error logging
      console.log('Location hash: ', location.hash)
      TEMP_LOG_FUNCTION(`Location hash: ${location.hash}`)
    }
  }, [location.hash])

  const logOutUser = useCallback(() => {
    localStorage.clear()
    resetUser()
  }, [resetUser])

  const generateTokenFromMsToken = useCallback(
    async (token: string) => {
      msTokenGenerate(token)
        .then(res => formatTokenResponseData(res))
        .then(tokenResponse => handleTokenResponse(tokenResponse))
        .catch(e => {
          // eslint-disable-next-line no-console -- TODO: Replace with proper error logging
          console.error('Generate token from ms token error: ', e)
          logOutUser()
        })
    },
    [handleTokenResponse, logOutUser],
  )

  const handleMsalLogin = async () => {
    const dbUsr = localStorage.getItem('db_usr')
    const parsedDbUser = dbUsr ? JSON.parse(dbUsr) : undefined
    const userEmail = parsedDbUser ? parsedDbUser.email : undefined

    const silentLoginRequest = {
      ...silentRequest,
      loginHint: userEmail,
    }

    setLocalLoad(true)

    try {
      // eslint-disable-next-line no-console -- TODO: Replace with proper error logging
      console.log('Attempting silent login')

      const ssoSilentResponse = await instance.ssoSilent(silentLoginRequest)
      await generateTokenFromMsToken(ssoSilentResponse.accessToken)
    } catch (err) {
      // eslint-disable-next-line no-console -- TODO: Replace with proper error logging
      console.error('Generate silent token error: ', err)
      TEMP_LOG_FUNCTION(`Generate silent token error: ${JSON.stringify(err)}`)

      try {
        // eslint-disable-next-line no-console -- TODO: Replace with proper error logging
        console.log('Attempting redirect login')
        await instance.loginRedirect(loginRequest)
      } catch (err) {
        // eslint-disable-next-line no-console -- TODO: Replace with proper error logging
        console.error('Redirect login error: ', err)
        TEMP_LOG_FUNCTION(`Redirect login error: ${JSON.stringify(err)}`)
      }
    } finally {
      setLocalLoad(false)
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper event type
  const _login = (e: any) => {
    e.preventDefault()
    delete axios.defaults.headers.common['Authorization']
    setLocalLoad(true)
    if (email && password) {
      login(email, password)
        .then(user => handleTokenResponse(user))
        .catch(e => {
          setError(e.message)
          setLocalLoad(false)
        })
    }
  }

  useEffect(() => {
    acquireMsalToken()
  }, [acquireMsalToken])

  if (localLoad) return <LoadingIndicator />

  return (
    <>
      <Content>
        <Flex justifyContent="center">
          <LoginCard>
            <LoginCardBody>
              <form onSubmit={_login}>
                <Flex flexDirection="column">
                  {error && <LoginError id="error">{error}</LoginError>}
                  <LoginLabel htmlFor="email">Email</LoginLabel>
                  <input
                    name="email"
                    type="email"
                    className="login-input"
                    onChange={e => setEmail(e.target.value)}
                  />
                  <LoginLabel htmlFor="password">Password</LoginLabel>
                  <input
                    name="password"
                    type="password"
                    className="login-input"
                    onChange={e => setPassword(e.target.value)}
                  />
                  <LoginButton type="submit" value="Login" />
                </Flex>
              </form>
              <ThirdPartySeparator>or</ThirdPartySeparator>
              <LoginWithMicrosoftButton onClick={handleMsalLogin} />
              <hr style={{ margin: '25px 0px' }} />
              <LoginMessage>
                <Link to="/request-reset">Forgot Password?</Link>
              </LoginMessage>
            </LoginCardBody>
          </LoginCard>
        </Flex>
      </Content>
    </>
  )
}
